import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import { useMediaQuery, useTheme } from "@mui/material";
import ModalBasicLayout from "../ModalBasicLayout";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import ModalCreateContentFolder from "../ModalCreateContentFolder";
import { useTranslationApp } from "../../../lib/i18next";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import { ALL_FILTER } from "../../../utils/constants";
import { getSelectedAccountKeys } from "../../../utils/sharePlatformAccounts";
import { useGalleryFolderSession } from "../../../hooks/useGalleryFolderSession";
import { useSharePlatformAccounts } from "../../../hooks/useSharePlatformAccounts";
import {
  AUTOMATIONS_BUTTON_BACK,
  AUTOMATIONS_BUTTON_CREATE,
  AUTOMATIONS_BUTTON_SAVE,
  AUTOMATIONS_VALIDATION_ACCOUNTS,
  AUTOMATIONS_VALIDATION_END_AFTER_START,
  AUTOMATIONS_VALIDATION_END_FUTURE,
  AUTOMATIONS_VALIDATION_END_REQUIRED,
  AUTOMATIONS_VALIDATION_FOLDER,
  AUTOMATIONS_VALIDATION_LANGUAGE,
  AUTOMATIONS_VALIDATION_START_FUTURE,
  AUTOMATIONS_VALIDATION_START_REQUIRED,
  AUTOMATIONS_VALIDATION_WINDOW,
  BUTTON_NEXT,
  ERROR,
  MODAL_CREATE_AUTOMATION_TITLE,
  MODAL_EDIT_AUTOMATION_TITLE,
} from "../../../i18n/keysTranslations";
import { SimpleAlert } from "../../Alerts/Alerts";
import AutomationModalHeader from "./AutomationModalHeader";
import AutomationStepper from "./AutomationStepper";
import StepSource from "./steps/StepSource";
import StepPlatforms from "./steps/StepPlatforms";
import StepSchedule from "./steps/StepSchedule";
import StepReview from "./steps/StepReview";
import {
  AUTOMATION_SOURCE_FOLDER,
  AUTOMATION_STEP_PLATFORMS,
  AUTOMATION_STEP_REVIEW,
  AUTOMATION_STEP_SCHEDULE,
  AUTOMATION_STEP_SOURCE,
  DEFAULT_AUTOMATION_FORM,
} from "./automationModalConstants";
import { buildDefaultAutomationName } from "./automationModalUtils";
import {
  buildCreateAutomationPayload,
  mapFormFromAutomation,
} from "../../../utils/publishingAutomationMappers";
import {
  buildDefaultValidityFields,
  getScheduleValidityMessage,
} from "../../../utils/publishingAutomationValidity";
import { AUTOMATION_EFFECTIVE_STATUS } from "../../../utils/publishingAutomationStatus";
import {
  CONTENT_LANGUAGE_PRESET,
  resolveContentLanguage,
} from "../../../utils/catalogs/contentLanguage";
import {
  useAdvertiserContentLanguage,
  useEnsureContentLanguageField,
} from "../../../hooks/useAdvertiserContentLanguage";

const AUTOMATION_MODAL_HEIGHT = "min(900px, calc(100vh - 48px))";
const CONFIG_STEPS = [
  AUTOMATION_STEP_SOURCE,
  AUTOMATION_STEP_PLATFORMS,
  AUTOMATION_STEP_SCHEDULE,
];

const getStepValidationMessage = (step, formState, t) => {
  switch (step) {
    case AUTOMATION_STEP_SOURCE:
      if (
        formState.sourceType === AUTOMATION_SOURCE_FOLDER &&
        !formState.folderID
      ) {
        return t(AUTOMATIONS_VALIDATION_FOLDER);
      }
      if (!formState.language) {
        return t(AUTOMATIONS_VALIDATION_LANGUAGE);
      }
      return null;
    case AUTOMATION_STEP_PLATFORMS:
      if (getSelectedAccountKeys(formState.platforms).length === 0) {
        return t(AUTOMATIONS_VALIDATION_ACCOUNTS);
      }
      return null;
    case AUTOMATION_STEP_SCHEDULE:
      if (
        !formState.startTime ||
        !formState.endTime ||
        formState.startTime >= formState.endTime
      ) {
        return t(AUTOMATIONS_VALIDATION_WINDOW);
      }
      return getScheduleValidityMessage({
        formState,
        t,
        isStartLocked: Boolean(formState.startLocked),
        keys: {
          startRequired: AUTOMATIONS_VALIDATION_START_REQUIRED,
          startFuture: AUTOMATIONS_VALIDATION_START_FUTURE,
          endRequired: AUTOMATIONS_VALIDATION_END_REQUIRED,
          endFuture: AUTOMATIONS_VALIDATION_END_FUTURE,
          endAfterStart: AUTOMATIONS_VALIDATION_END_AFTER_START,
        },
      });
    case AUTOMATION_STEP_REVIEW:
      return null;
    default: {
      const _exhaustive = step;
      void _exhaustive;
      return null;
    }
  }
};

const getFirstMissingConfig = (formState, t) => {
  for (const step of CONFIG_STEPS) {
    const message = getStepValidationMessage(step, formState, t);
    if (message) {
      return { step, message };
    }
  }
  return null;
};

const ModalCreateAutomation = ({
  modalOpen,
  onCloseModal,
  onSubmitAutomation,
  onCommitName,
  businessName,
  editingAutomation = null,
  liveEditingAutomation = null,
  canPost = true,
  onToggleActive,
  allowedPlatforms = null,
  allowedFrequencies = null,
  onLockedCapability = () => {},
}) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompactLayout = useMediaQuery(theme.breakpoints.down("md"));
  const { accounts } = useSharePlatformAccounts();
  const [currentStep, setCurrentStep] = useState(AUTOMATION_STEP_SOURCE);
  const isEditMode = Boolean(editingAutomation);
  const switchAutomation = liveEditingAutomation || editingAutomation;
  const isSwitchOff =
    switchAutomation?.effectiveStatus === AUTOMATION_EFFECTIVE_STATUS.OFF;

  const {
    control,
    formState: { errors },
    reset: resetAutomationForm,
    setValue,
    getValues,
    watch,
  } = useForm({
    defaultValues: DEFAULT_AUTOMATION_FORM,
  });

  const formState = watch();
  const { storedLanguage, platformLanguage } = useAdvertiserContentLanguage();

  useEnsureContentLanguageField({
    preset: CONTENT_LANGUAGE_PRESET.ALL,
    setValue,
    getValues,
    contextLanguage: editingAutomation?.language || null,
    enabled: modalOpen,
  });

  const noop = useCallback(() => {}, []);

  const {
    folders,
    isLoadingFolders,
    isCreatingFolder,
    isOpenCreateFolderModal,
    openCreateFolderModal,
    closeCreateFolderModal,
    onCreateFolderInContext,
    createFolderParentID,
    createFolderParentLabel,
    getFolderLabel,
  } = useGalleryFolderSession({
    modalOpen,
    folderFilterID: ALL_FILTER,
    onFolderFilterChange: noop,
    setData: noop,
    clearSelection: noop,
  });

  const getInitialFormState = useCallback(() => {
    const defaultLanguage = resolveContentLanguage({
      storedLanguage,
      preset: CONTENT_LANGUAGE_PRESET.ALL,
      platformLanguage,
    });
    const frequencyKey = (() => {
      const current = editingAutomation
        ? mapFormFromAutomation(editingAutomation).frequencyKey
        : DEFAULT_AUTOMATION_FORM.frequencyKey;
      if (
        !editingAutomation &&
        Array.isArray(allowedFrequencies) &&
        allowedFrequencies.length > 0 &&
        !allowedFrequencies.includes(current)
      ) {
        return allowedFrequencies[0];
      }
      return current;
    })();
    if (editingAutomation) {
      const mapped = mapFormFromAutomation(editingAutomation);
      return {
        ...DEFAULT_AUTOMATION_FORM,
        ...mapped,
        frequencyKey,
        language: mapped.language || defaultLanguage,
      };
    }
    return {
      ...DEFAULT_AUTOMATION_FORM,
      ...buildDefaultValidityFields(),
      name: buildDefaultAutomationName(t),
      language: defaultLanguage,
      frequencyKey,
    };
  }, [
    allowedFrequencies,
    editingAutomation,
    platformLanguage,
    storedLanguage,
    t,
  ]);

  useEffect(() => {
    if (!modalOpen) return;
    setCurrentStep(AUTOMATION_STEP_SOURCE);
    resetAutomationForm(getInitialFormState());
  }, [getInitialFormState, modalOpen, resetAutomationForm]);

  const onChangeForm = useCallback(
    (patch) => {
      Object.entries(patch).forEach(([key, value]) => {
        setValue(key, value);
      });
    },
    [setValue]
  );

  const onCommitHeaderName = useCallback(
    async (name) => {
      const previousName = String(formState.name || "").trim();
      const nextName = String(name || "").trim();
      if (!nextName) {
        return;
      }

      onChangeForm({ name: nextName });
      if (!isEditMode || !canPost || !editingAutomation?.id) {
        return;
      }
      if (nextName === previousName) {
        return;
      }

      const response = await onCommitName?.(nextName);
      if (response && response.ok === false) {
        onChangeForm({ name: previousName });
      }
    },
    [
      canPost,
      editingAutomation?.id,
      formState.name,
      isEditMode,
      onChangeForm,
      onCommitName,
    ]
  );

  const onResetAndClose = () => {
    setCurrentStep(AUTOMATION_STEP_SOURCE);
    resetAutomationForm(getInitialFormState());
    onCloseModal();
  };

  const showValidationAlert = (text) => {
    SimpleAlert({
      icon: "error",
      title: t(ERROR),
      text,
    });
  };

  const onStepClick = (stepNumber) => {
    if (stepNumber <= currentStep) {
      setCurrentStep(stepNumber);
      return;
    }

    for (const step of CONFIG_STEPS) {
      if (step >= stepNumber) break;
      const message = getStepValidationMessage(step, formState, t);
      if (message) {
        showValidationAlert(message);
        setCurrentStep(step);
        return;
      }
    }

    setCurrentStep(stepNumber);
  };

  const onBack = () => {
    if (currentStep > AUTOMATION_STEP_SOURCE) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const onCreateFolder = async ({ name, parentFolderID }) => {
    const response = await onCreateFolderInContext({ name, parentFolderID });

    if (response?.ok && response?.data?.id) {
      setValue("folderID", response.data.id, { shouldValidate: true });
      closeCreateFolderModal();
    }

    return response;
  };

  const onNext = async () => {
    const isLastStep = currentStep === AUTOMATION_STEP_REVIEW;

    if (isLastStep) {
      const missing = getFirstMissingConfig(formState, t);
      if (missing) {
        showValidationAlert(missing.message);
        setCurrentStep(missing.step);
        return;
      }

      if (!canPost) return;

      const selectedPlatforms = accounts
        .filter((account) => formState.platforms?.[account.key])
        .map((account) => account.platform);
      const platformBlocked =
        Array.isArray(allowedPlatforms) &&
        selectedPlatforms.some(
          (platform) => !allowedPlatforms.includes(platform)
        );
      const frequencyBlocked =
        Array.isArray(allowedFrequencies) &&
        !allowedFrequencies.includes(formState.frequencyKey);
      if (platformBlocked || frequencyBlocked) {
        onLockedCapability();
        return;
      }

      const payload = buildCreateAutomationPayload({
        formState: {
          ...formState,
          name: formState.name || buildDefaultAutomationName(t),
        },
        accounts,
      });

      await onSubmitAutomation(payload);
      return;
    }

    const validationMessage = getStepValidationMessage(
      currentStep,
      formState,
      t
    );
    if (validationMessage) {
      showValidationAlert(validationMessage);
      return;
    }

    setCurrentStep((prev) => prev + 1);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case AUTOMATION_STEP_SOURCE:
        return (
          <StepSource
            formState={formState}
            onChange={onChangeForm}
            businessName={businessName}
            control={control}
            errors={errors}
            folders={folders}
            isLoadingFolders={isLoadingFolders}
            isCreatingFolder={isCreatingFolder}
            onOpenCreateFolder={openCreateFolderModal}
          />
        );
      case AUTOMATION_STEP_PLATFORMS:
        return (
          <StepPlatforms
            formState={formState}
            onChange={onChangeForm}
            businessName={businessName}
            allowedPlatforms={allowedPlatforms}
            onLockedCapability={onLockedCapability}
          />
        );
      case AUTOMATION_STEP_SCHEDULE:
        return (
          <StepSchedule
            control={control}
            errors={errors}
            businessName={businessName}
            formState={formState}
            onChange={onChangeForm}
            allowedFrequencies={allowedFrequencies}
            onLockedCapability={onLockedCapability}
          />
        );
      case AUTOMATION_STEP_REVIEW:
        return (
          <StepReview formState={formState} getFolderLabel={getFolderLabel} />
        );
      default:
        return null;
    }
  };

  const isLastStep = currentStep === AUTOMATION_STEP_REVIEW;

  return (
    <>
      <ModalBasicLayout
        modalOpen={modalOpen}
        onCloseModal={onResetAndClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        title=""
        titleContent={false}
        customComponentTitle={
          <AutomationModalHeader
            name={formState.name}
            onRename={onCommitHeaderName}
            canEditName={canPost}
            showToggle={Boolean(isEditMode && switchAutomation)}
            isActive={switchAutomation?.active}
            isToggleDisabled={!canPost || isSwitchOff}
            onToggleActive={(checked) =>
              onToggleActive?.(switchAutomation.id, checked)
            }
          />
        }
        ariaLabel={t(
          isEditMode
            ? MODAL_EDIT_AUTOMATION_TITLE
            : MODAL_CREATE_AUTOMATION_TITLE
        )}
        sxPaper={{
          display: "flex",
          flexDirection: "column",
          height: isMobile ? "100%" : AUTOMATION_MODAL_HEIGHT,
          maxHeight: isMobile ? "100%" : AUTOMATION_MODAL_HEIGHT,
          width: isMobile ? "100%" : undefined,
          m: isMobile ? 0 : undefined,
          borderRadius: isMobile ? 0 : undefined,
        }}
        sxTitle={{
          color: "background.paperContrastText",
          justifyContent: "flex-start",
          width: "100%",
          minWidth: 0,
          px: { xs: 1.5, sm: 3 },
          pt: { xs: 1.5, sm: 2 },
          pb: { xs: 1.5, sm: 2 },
          gap: 1,
          borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
          flexShrink: 0,
        }}
        sxContent={{
          px: 0,
          pb: 0,
          pt: 0,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "180px minmax(0, 1fr)" },
            gridTemplateRows: {
              xs: "auto minmax(0, 1fr)",
              md: "minmax(0, 1fr)",
            },
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          <AutomationStepper
            currentStep={currentStep}
            onStepClick={onStepClick}
            onBack={onBack}
            isBackDisabled={currentStep === AUTOMATION_STEP_SOURCE}
            variant={isCompactLayout ? "horizontal" : "vertical"}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              minWidth: 0,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflowY: "auto",
                overflowX: "hidden",
                px: { xs: 2, sm: 4 },
                py: { xs: 2, sm: 3 },
              }}
            >
              {renderStepContent()}
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                px: { xs: 2, sm: 4 },
                py: 2,
                borderTop: `1px solid ${INPUT_COLOR_BORDER}`,
                backgroundColor: "background.paper",
              }}
            >
              <ContainerModalActions
                hasPadding={false}
                sx={{
                  ...(isCompactLayout ? {} : { justifyContent: "flex-end" }),
                }}
                propsSecondaryButton={{
                  showButton: isCompactLayout,
                  text: AUTOMATIONS_BUTTON_BACK,
                  onClick: onBack,
                  disabled: currentStep === AUTOMATION_STEP_SOURCE,
                }}
                propsPrimaryButton={{
                  showButton: true,
                  text: isLastStep
                    ? isEditMode
                      ? AUTOMATIONS_BUTTON_SAVE
                      : AUTOMATIONS_BUTTON_CREATE
                    : BUTTON_NEXT,
                  onClick: onNext,
                  disabled: isLastStep && !canPost,
                }}
              />
            </Box>
          </Box>
        </Box>
      </ModalBasicLayout>

      <ModalCreateContentFolder
        modalOpen={isOpenCreateFolderModal}
        folders={folders}
        parentFolderID={createFolderParentID}
        parentFolderLabel={createFolderParentLabel}
        isLoading={isCreatingFolder}
        onCloseModal={closeCreateFolderModal}
        onCallbackCreate={onCreateFolder}
      />
    </>
  );
};

export default ModalCreateAutomation;
